import type { MouseEvent } from 'react';
import { inboxLocators } from '@locators/inbox';
import { Button } from '@servicetitan/anvil2';
import { TaskVoiceCardAction } from '../action-utils';
import styles from './TaskVoiceActions.module.css';
import ActionsRow from '../../TaskRow/ActionsRow/ActionsRow';
import { getTestIdProps } from '@lib/testUtils';
import useTaskActions from '@modules/TasksModule/hooks/useTaskActions';
import useBookingRetentionEnabled from '@modules/InboxModule/hooks/useBookingRetentionEnabled';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import InboxModule from '@modules/InboxModule';
import useIsBookingGuarded from '../../CustomerMfeAdapter/useIsBookingGuarded';
import { useCloseEndedCallHandler } from './useCloseEndedCallHandler';

interface TaskVoiceActionsProps {
  taskId: string;
  /** The task's stable view key, for the Resume Booking booking check. */
  viewKey?: string;
  action?: TaskVoiceCardAction | null;
  isAnswering: boolean;
  isConnecting: boolean;
  isOffered: boolean;
  type: string;
  /**
   * The card shows Close and Dismiss as an X in its header instead
   * (TaskVoiceCloseButton), so this row leaves them out. Resume Booking stays.
   */
  closeInHeader?: boolean;
}

export const TaskVoiceActions = ({
  taskId,
  viewKey,
  action,
  isAnswering,
  isConnecting,
  isOffered,
  type,
  closeInHeader = false,
}: TaskVoiceActionsProps) => {
  const { handleAccept, handleReject, handleComplete, handleResume } =
    useTaskActions(taskId);
  const isRetentionEnabled = useBookingRetentionEnabled();
  const { selectedTaskId } = useInboxState();
  const isBookingGuarded = useIsBookingGuarded(
    isRetentionEnabled ? viewKey : undefined,
  );
  // a hidden card with a booking open offers to bring it back; the selected
  // card already shows it. Legacy views never register, so never offer it.
  const canResumeBooking = isBookingGuarded && selectedTaskId !== taskId;

  const handleClose = useCloseEndedCallHandler(taskId);

  const handleResumeBooking = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    InboxModule.selectTaskByUser(taskId);
  };

  switch (action) {
    case TaskVoiceCardAction.activeCall:
      return (
        <ActionsRow
          isAnswering={isAnswering}
          callConnecting={isConnecting && isOffered}
          offered={isOffered}
          type={type}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      );
    case TaskVoiceCardAction.endedCall:
      if (closeInHeader && !canResumeBooking) {
        return null;
      }
      return (
        <div className={styles.buttonsContainer}>
          {!closeInHeader && (
            <Button
              size='small'
              onClick={handleClose}
              {...getTestIdProps(inboxLocators.taskList.closeButton)}
            >
              Close
            </Button>
          )}
          {canResumeBooking && (
            <Button
              appearance='primary'
              size='small'
              onClick={handleResumeBooking}
              {...getTestIdProps(inboxLocators.taskList.resumeBookingButton)}
            >
              Resume Booking
            </Button>
          )}
        </div>
      );
    case TaskVoiceCardAction.missedCall:
      if (closeInHeader) {
        return null;
      }
      return (
        <div className={styles.buttonsContainer}>
          <Button
            size='small'
            onClick={handleComplete}
            {...getTestIdProps(inboxLocators.taskList.dismissButton)}
          >
            Dismiss
          </Button>
        </div>
      );
    case TaskVoiceCardAction.heldCall:
      return (
        <div className={styles.buttonsContainer}>
          <Button
            size='small'
            onClick={handleResume}
            {...getTestIdProps(inboxLocators.taskList.resumeButton)}
          >
            Resume Call
          </Button>
        </div>
      );
    default:
      return null;
  }
};
